import { DatePipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';

import { take } from 'rxjs';

import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';

import { ChatMessage } from '@lib/chat/models/chat.interface';
import { ChatCommunicationService } from '@lib/chat/services/chat-communication.service';
import { parseLinksInText } from '../../../utils/link-parser.util';

import { CreateAppointmentModalType } from '@lib/core/models/create-appointment.interface';

import { SpinLoadingComponent } from '@lib/shared-ui/commons/spin-loading/spin-loading.component';

import { CHAT_NAVIGATION_HANDLERS } from '../../../core/tokens/chat-navigation.tokens';

// TODO: create message state handler with corresponding icons
enum MessageState {
  Pending = 'pending',
  Sent = 'sent',
  Failed = 'failed',
  Read = 'read',
}

@Component({
  selector: 'chat-message',
  standalone: true,
  imports: [NgClass, DatePipe, MatIcon, MatMenu, MatMenuItem, MatMenuTrigger, SpinLoadingComponent],
  templateUrl: './chat-message.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatMessageComponent {
  private readonly chatCommunicationService = inject(ChatCommunicationService);
  private readonly navigationHandlers = inject(CHAT_NAVIGATION_HANDLERS);

  protected readonly MessageState = MessageState;

  readonly index = input.required<number>();
  readonly ownUserId = input.required<string>();
  readonly threadUserId = input.required<string>();
  readonly message = input.required<ChatMessage>();

  readonly retryMessage = output<string>();

  protected readonly isMyMessage = computed<boolean>(
    () => this.message().userId === '00000000-0000-0000-0000-000000000000' || this.message().userId === this.ownUserId() || this.message().metadata?.['isLilo'] === 'true'
  );
  protected readonly appointmentCreationAvailable = computed<boolean>(
    () =>
      !this.isMyMessage() && !!this.message().metadata['special_message_type'] && (!!this.message().metadata?.['prescriptionId'] || !!this.message().metadata?.['prescriptionIds'])
  );
  protected readonly messageContentWithLinks = computed<string>(() => parseLinksInText(this.message().content));
  protected readonly isPendingMessage = computed<boolean>(() => !!this.message().isPending);
  protected readonly isFailedMessage = computed<boolean>(() => !!this.message().hasFailed);

  getUserReadReceipt() {
    const userReadReceipt = Object.entries(this.message().readReceipts).find(entry => entry[0] == this.threadUserId());

    return {
      readTime: userReadReceipt ? userReadReceipt[1] : null,
      isRead: !!userReadReceipt,
    };
  }

  getState() {
    const message = this.message();
    const userReadReceipt = this.getUserReadReceipt();

    if (message.isPending) return MessageState.Pending;
    if (message.hasFailed) return MessageState.Failed;
    if (userReadReceipt.isRead) return MessageState.Read;
    else return MessageState.Sent;
  }

  getFiles() {
    return Object.entries(this.message().media)
      .filter(([key]) => !!this.message().media[key])
      .map(([key]) => key);
  }

  downloadFile(file: string) {
    this.chatCommunicationService
      .getFileContents(this.message().media[file])
      .pipe(take(1))
      .subscribe(data => {
        const url = window.URL.createObjectURL(data);
        window.open(url);
      });
  }

  isUpdated() {
    return Math.abs(this.message().updatedAt.getTime() - this.message().createdAt.getTime()) > 1000;
  }

  navigateToPrescriptionsTab(prescriptionId: string) {
    this.navigationHandlers.switchToPrescriptionTab(prescriptionId);
  }

  navigateToPrescriptionsPage(prescriptionId: string) {
    this.navigationHandlers.navigateToPrescription(prescriptionId, this.threadUserId());
  }

  openCreateManualAppointmentModal() {
    const metadata = this.message().metadata;

    if ((metadata?.['prescriptionId'] || metadata?.['prescriptionIds']) && metadata?.['partnerId'] && metadata?.['locationId']) {
      // Parse prescriptionIds if it's a JSON string
      let parsedPrescriptionIds = metadata['prescriptionIds'];
      if (typeof parsedPrescriptionIds === 'string') {
        try {
          parsedPrescriptionIds = JSON.parse(parsedPrescriptionIds);
        } catch (error) {
          console.error('Error parsing prescriptionIds:', error);
          parsedPrescriptionIds = [];
        }
      }

      this.navigationHandlers.openCreateAppointmentModal?.(this.threadUserId(), CreateAppointmentModalType.ManualCreation, {
        prescriptionId: metadata['prescriptionId'],
        prescriptionIds: parsedPrescriptionIds,
        prescriptionName: metadata['prescriptionName'],
        partnerId: metadata['partnerId'],
        locationId: metadata['locationId'],
        messageType: metadata['special_message_type'],
      });
    } else this.navigationHandlers.openCreateAppointmentModal?.(this.threadUserId(), CreateAppointmentModalType.ManualCreation);
  }

  onRetryMessage() {
    if (this.isFailedMessage()) this.retryMessage.emit(this.message().id);
  }
}
